import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Table, Button, Modal, Form, Select, Input, Space, Tag, message, Typography, Popconfirm, Statistic, Row, Col, Tooltip } from "antd";
import Alert from "../components/GxAlert";
import { PlusOutlined, DeleteOutlined, DollarOutlined, EditOutlined, FilePdfOutlined, ReloadOutlined, SwapOutlined } from "@ant-design/icons";
import TransferModal from "../components/TransferModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getEmployees, getPeriods, getFinanceTxns, getFinanceDocuments, createFinanceTxn, deleteFinanceTxn,
  setTxnPaymentStatus,
  getEmployeeLedger, getDebts, createDebt, repayDebt, carryForwardDebt,
  updateFinanceTxn,
} from "../api/client";
import usePdfPreview from "../utils/usePdfPreview";
import SourceSelect, { OFFICE, sourceLabel } from "../components/SourceSelect";
import VoucherModal, { KIND_COLOR, KIND_LABEL, kindOfType } from "../components/VoucherModal";
import GxTable from "../components/GxTable";
import GxTabs from "../components/GxTabs";
import EditableTable from "../components/EditableTable";
import { MoneyInput, formatMoney } from "../utils/money";
import { byNum, byStr } from "../utils/sorters";
import { JalaliDateInput, todayISO, toJalali, currentJalali, JALALI_MONTHS,
         jalaaliToISO, jalaaliMonthLength } from "../utils/dateUtils";
import { filterByChildren } from "../utils/selectFilter";
import BalancesTab from "./finance/BalancesTab";
import LedgerHealthTab from "./finance/LedgerHealthTab";
import useRefreshOnShow from "../utils/useRefreshOnShow";
import SalaryPaymentsTab from "./finance/SalaryPaymentsTab";
import AdvanceBatchTab from "./finance/AdvanceBatchTab";
import PettyCashTab from "./finance/PettyCashTab";
import FinanceReportsTab from "./finance/FinanceReportsTab";
import { useAuth } from "../context/AuthContext";
import { getActiveYm, pickActivePeriod, rememberPeriod, setActiveYm } from "../utils/activeMonth";

const { Title, Text } = Typography;
const { Option } = Select;

// انواع تراکنش — مساعده/کسورات/مزایا همه در همین دفتر (منبع واحد برای accounting_v1)
const TXN_TYPES = [
  { v: "previous_debt", l: "بدهی قبلی", dir: "debit" },
  { v: "advance", l: "مساعده", dir: "debit" },
  { v: "bank_transfer", l: "حوالهٔ بانکی (پرداخت‌شده)", dir: "debit" },
  { v: "check_payment", l: "چک (پرداخت‌شده)", dir: "debit" },
  { v: "salary_paid", l: "حقوقِ پرداخت‌شده", dir: "debit" },
  { v: "defective_product_purchase", l: "خرید محصول زده‌دار", dir: "debit" },
  { v: "product_purchase", l: "خرید محصول / کالا", dir: "debit" },
  { v: "buffet_purchase", l: "خرید بوفه / سوپرمارکت", dir: "debit" },
  { v: "penalty", l: "جریمه", dir: "debit" },
  { v: "manual_deduction", l: "کسر دستی", dir: "debit" },
  { v: "absence_manual", l: "کسر غیبت دستی", dir: "debit" },
  { v: "shortage_manual", l: "کسر کسری‌کار دستی", dir: "debit" },
  { v: "cash_repayment", l: "بازپرداخت نقدی بدهی", dir: "credit" },
  { v: "manual_credit", l: "بستانکاری دستی", dir: "credit" },
  { v: "reward", l: "پاداش", dir: "credit" },
  { v: "overtime_bonus", l: "اضافه‌کار دستی / پاداش اضافه‌کار", dir: "credit" },
  { v: "refund", l: "بازگشت وجه", dir: "credit" },
];
// انتقال بین پرسنل فقط از پنجرهٔ خودش ساخته می‌شود ⇒ در فرمِ سند نیست، ولی برچسبِ فارسی می‌خواهد (v2.136.0)
/* v2.141.0 — «ثبت از»: سندِ دستی نارنجی و برجسته، تا بشود سندِ دستیِ تکراری را از پرداختِ «پرداخت حقوق» تشخیص داد */
const ORIGIN_COLOR = { "سندِ دستی": "orange", "پرداخت حقوق": "blue", "پرداخت حقوق — بیمه": "geekblue",
  "انتقال بین پرسنل": "purple", "مساعده": "cyan" };
const originTag = (v) => (v ? <Tag color={ORIGIN_COLOR[v] || "default"} style={{ margin: 0 }}>
  {v === "سندِ دستی" ? "✍ سندِ دستی" : v}</Tag> : "—");
const TXN_LABEL = { ...Object.fromEntries(TXN_TYPES.map(t => [t.v, t.l])),
  transfer_out: "انتقال به حسابِ همکار", transfer_in: "دریافت از حسابِ همکار",
  payroll_adjustment: "قلمِ حقوقی (محاسبهٔ حقوق)" };
/** «همهٔ تاریخ» + ماه‌های سالِ جاری و سالِ قبل — دفتر باید کامل باز شود. */
const TXN_RANGES = (() => {
  const { year } = currentJalali();
  const out = [{ value: "all", label: "همهٔ تاریخ" }];
  [year, year - 1].forEach((y) => {
    JALALI_MONTHS.forEach((m, i) => out.push({ value: `${y}-${i + 1}`, label: `${m} ${y}` }));
  });
  return out;
})();
const fa = (n) => formatMoney(n);

export default function FinancialLedgerPage() {
  const { can } = useAuth();
  const canEdit = can("accounting.edit");
  const [searchParams, setSearchParams] = useSearchParams();
  // تبِ پیش‌فرض «مانده‌ها»ست — اولین چیزی که حسابدار می‌خواهد بداند.
  // تب‌های قدیمی به مقصدِ تازه هدایت می‌شوند تا لینک‌های ذخیره‌شده نشکنند.
  const LEGACY_TAB = { debts: "balances" };
  const rawTab = searchParams.get("tab") || "balances";
  const activeTab = LEGACY_TAB[rawTab] || rawTab;
  /* `push` نه `replace` — «برگشت»ِ مرورگر باید به تبِ قبلیِ همین صفحه برگردد،
     نه به صفحهٔ قبلیِ منو. شخصِ دفتر هم در همان آدرس می‌نشیند تا برگشت/جلو
     دقیقاً همان حسابی را باز کند که کاربر دیده بود. */
  const setUrl = (patch, opts) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    return next;
  }, { replace: !!(opts && opts.replace) });
  const setTab = (key) => setUrl({ tab: key });
  const ledgerEmp = Number(searchParams.get("emp")) || null;
  /** کلیک روی نامِ هر شخص ⇒ همان‌جا دفترِ حسابِ خودش باز شود. */
  const goLedger = (id) => setUrl({ tab: "ledger", emp: id });
  const [employees, setEmployees] = useState([]);
  // پیش‌نمایشِ صورتحسابِ PDF قبل از دانلود/چاپ
  const pdf = usePdfPreview();
  const [periods, setPeriods] = useState([]);
  const [empId, setEmpId] = useState(null);
  const [periodId, setPeriodId] = useState(null);
  const [txns, setTxns] = useState([]);
  const [debts, setDebts] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [debtModal, setDebtModal] = useState(false);
  const [debtForm] = Form.useForm();

  useEffect(() => {
    getEmployees({ limit: 1000 }).then(r => setEmployees(Array.isArray(r.data) ? r.data : (r.data.items || [])));
    // ماهِ فعالِ مشترک (v2.132.0) — نه همیشه اولین دوره
    getPeriods().then(r => { setPeriods(r.data); const p = pickActivePeriod(r.data); if (p) setPeriodId(p.id); });
  }, []);

  /* 🔴 قبلاً: `if (periodId) … { period_id: periodId }`.
     یعنی بدونِ دورهٔ انتخاب‌شده هیچ‌چیز بارگذاری نمی‌شد، و **با** دوره هم فقط
     تراکنش‌هایی می‌آمدند که به آن دوره وصل‌اند. ولی بازپرداختِ بدهی و
     بستانکاریِ ابتدای دوره اصلاً دوره ندارند — پس دفتر همیشه خالی به‌نظر
     می‌رسید. حالا پیش‌فرض **کلِ تاریخ** است و ماه فقط یک فیلترِ اختیاری. */
  const [txnRange, setTxnRange] = useState("all");     // all | <year>-<month>
  /* جستجوی پرسنل در همین نوارِ فیلتر — خواستهٔ کاربر. فیلتر **سمتِ سرور**
     اعمال می‌شود، نه روی ردیف‌های بارگذاری‌شده: وگرنه جمع‌ها و «ماندهٔ تا این
     سند» با چیزی که روی صفحه دیده می‌شود نمی‌خواند. */
  const [txnEmp, setTxnEmp] = useState(null);
  const txnParams = useMemo(() => {
    const base = txnEmp ? { employee_id: txnEmp } : {};
    if (txnRange === "all") return base;
    const [y, m] = String(txnRange).split("-").map(Number);
    if (!y || !m) return base;
    return {
      ...base,
      date_from: jalaaliToISO(y, m, 1),
      date_to: jalaaliToISO(y, m, jalaaliMonthLength(y, m)),
    };
  }, [txnRange, txnEmp]);
  const [docTotals, setDocTotals] = useState(null);
  const [payDate, setPayDate] = useState(null);   // {row, when}
  const loadTxns = useCallback(() => {
    // دفترِ اسناد: تراکنش‌ها **به‌علاوهٔ** اصلِ بدهی‌ها. اندپوینتِ قبلی فقط
    // جدولِ تراکنش‌ها را می‌خواند، پس مانده‌های ابتدای دوره اصلاً دیده
    // نمی‌شدند — در حالی که آن‌ها هم سندِ مالی‌اند.
    getFinanceDocuments(txnParams)
      // _seq = ترتیبِ منطقیِ سرور (جدید بالا، و در یک روز: پرداخت‌ها بالای «طلبِ حقوق») — مرتب‌سازیِ تاریخ آن را نگه می‌دارد
      .then((r) => { setTxns((r.data.rows || []).map((x, i) => ({ ...x, _seq: i })));
        setDocTotals(r.data.totals || null); })
      .catch(() => {});
  }, [txnParams]);
  const loadDebts = () => { if (periodId) getDebts({ period_id: periodId }).then(r => setDebts(r.data)); };
  useEffect(() => { loadDebts(); /* eslint-disable-next-line */ }, [periodId]);
  useEffect(() => { loadTxns(); }, [loadTxns]);

  const empName = (id) => employees.find(e => e.id === id)?.full_name || `#${id}`;
  const periodLabel = (p) => `${p.year}/${String(p.month).padStart(2, "0")}`;

  /* سندِ واحدِ پرداخت/دریافت — به‌جای فرمِ قدیمیِ «افزودن تراکنش» با ۱۷ نوعِ گنگ */
  const [voucher, setVoucher] = useState(null);       // null | initial
  const [transferOpen, setTransferOpen] = useState(false);
  const navigate = useNavigate();
  const [payFocus, setPayFocus] = useState(null);   // v2.140.0: بازکردنِ پرداختِ یک نفر در «پرداخت حقوق»
  const afterVoucher = () => {
    loadTxns();
    if (ledgerEmp) loadLedger(ledgerEmp, ledgerFrom, ledgerTo);
    loadDebts();
  };

  const submitDebt = async (vals) => {
    try {
      await createDebt({ employee_id: vals.employee_id, amount: vals.amount,
        current_period_id: periodId, note: vals.note });
      message.success("بدهی ثبت شد"); setDebtModal(false); debtForm.resetFields(); loadDebts();
    } catch { message.error("خطا"); }
  };

  /* بازپرداختِ بدهی با فرم (مبلغ، تاریخ، مقصدِ پول) — قبلاً یک prompt بود که نه تاریخ داشت نه مبدأ */
  const [repayFor, setRepayFor] = useState(null);
  const [repayForm] = Form.useForm();
  const doRepay = (d) => {
    setRepayFor(d);
    repayForm.setFieldsValue({ amount: Number(d.remaining_amount) || null, occurred_at: todayISO(),
      source: OFFICE, note: "بازپرداخت بدهی" });
  };
  const submitRepay = async (v) => {
    try {
      await repayDebt(repayFor.id, { amount: v.amount, occurred_at: v.occurred_at || todayISO(),
        note: v.note || null, ...(v.source || OFFICE) });
      message.success("بازپرداخت ثبت شد"); setRepayFor(null); loadDebts(); loadTxns();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };
  const doCarry = async (d) => {
    const others = periods.filter(p => p.id !== periodId);
    if (!others.length) { message.warning("دورهٔ مقصدی موجود نیست"); return; }
    try { await carryForwardDebt(d.id, { to_period_id: others[0].id }); message.success("منتقل شد"); loadDebts(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const [ledgerFrom, setLedgerFrom] = useState(null);
  const [ledgerTo, setLedgerTo] = useState(null);
  /* شخصِ دفتر state نیست، **آدرس** است. قبلاً کلیک روی نام
     `openLedger(...) || setTab("ledger")` بود؛ چون `openLedger` یک Promise
     (مقدارِ truthy) برمی‌گرداند، `||` هیچ‌وقت به `setTab` نمی‌رسید ⇒ داده
     بارگذاری می‌شد ولی تب عوض نمی‌شد و کاربر باید دستی می‌رفت روی «دفتر حساب
     شخص». حالا یک تغییرِ آدرس هر دو کار را با هم می‌کند. */
  const loadLedger = useCallback((id, from, to) => {
    if (!id) { setLedger(null); return Promise.resolve(); }
    return getEmployeeLedger(id, {
      from_date: from || undefined,
      // `as_of` یعنی «تا این تاریخ» — سرویس ماندهٔ ابتدای بازه را هم خودش
      // حساب می‌کند، پس صورت‌حسابِ وسطِ ماه هم درست درمی‌آید.
      as_of: to || undefined,
    }).then((r) => setLedger(r.data));
  }, []);
  useEffect(() => { loadLedger(ledgerEmp, ledgerFrom, ledgerTo); },
    [ledgerEmp, ledgerFrom, ledgerTo, loadLedger]);
  // v2.167.0 — تب‌های زنده‌مانده با هر بار دیده‌شدن تازه می‌شوند (کاربر: «فاکتور زده بودم هنوز به‌روز نبود»)
  useRefreshOnShow(loadTxns, activeTab === "txns");
  useRefreshOnShow(() => loadLedger(ledgerEmp, ledgerFrom, ledgerTo), activeTab === "ledger");

  /** ستون‌های ریزِ حساب: مثلِ تراکنش‌ها ولی با **ماندهٔ تجمعی**. */
  const ledgerCols = [
    /* شمارهٔ سند = لینک به همان سندِ واحد؛ همین‌جا باز می‌شود تا بشود بررسی/ویرایش کرد
       بدونِ اینکه کاربر برود اسنادِ مالی و دوباره بگردد. */
    { title: "شماره سند", dataIndex: "doc_no", width: 130,
      render: (v, r) => (
        <Tooltip title={r.kind === "debt" ? "بدهی — در بخشِ بدهی‌ها" : "بازکردنِ سند برای بررسی/ویرایش"}>
          <a onClick={() => goSource({ ...r, employee_id: r.employee_id ?? ledgerEmp })}
            style={{ fontFamily: "monospace", fontSize: 12 }}>
            {v || (r.kind === "debt" ? "بدهی" : `#${r.id}`)} <EditOutlined style={{ fontSize: 11 }} />
          </a>
        </Tooltip>
      ) },
    { title: "تاریخ", dataIndex: "date", width: 110, render: (v) => toJalali(v) },
    { title: "ثبت از", dataIndex: "origin", width: 130, render: originTag },
    {
      title: "شرح", dataIndex: "type", width: 200,
      render: (v, r) => (
        <Space size={4}>
          <span>{TXN_LABEL[v] || (v === "debt_origination" ? "ایجاد بدهی" : v)}</span>
          {r.is_synthetic && <Tag color="orange">بدهی</Tag>}
        </Space>
      ),
    },
    { title: "توضیح", dataIndex: "description", width: 180, render: (v) => v || "—" },
    {
      title: "بدهکار", dataIndex: "signed", width: 130, align: "left",
      render: (v) => (v > 0 ? <span style={{ color: "#C0392B" }}>{fa(v)}</span> : "—"),
    },
    {
      title: "بستانکار", dataIndex: "signed", width: 130, align: "left",
      render: (v) => (v < 0 ? <span style={{ color: "#107C41" }}>{fa(-v)}</span> : "—"),
    },
    {
      title: "مانده تا این ردیف", dataIndex: "running_balance", width: 150, align: "left",
      render: (v) => (
        <b style={{ color: v > 0 ? "#C0392B" : v < 0 ? "#107C41" : undefined }}>
          {fa(Math.abs(v))}{v ? (v > 0 ? " بد" : " بس") : ""}
        </b>
      ),
    },
  ];

  /** ویرایش را به همان جایی می‌برد که سند آنجا ساخته شده. */
  const goSource = (r) => {
    // v2.144.0 — ردیفِ «محاسبه و بستن حقوق» ⇒ همان صفحه، همان ماه، ویرایشگرِ همان نفر
    if (r.kind === "adj") return navigate(`/payroll?period_id=${r.period_id}&employee_id=${r.employee_id}`);
    if (r.kind === "debt") return setTab("debts_legacy");
    if (r.type === "advance") return setTab("advances");
    // انتقال بین پرسنل دو سندِ جفت است ⇒ فقط از پنجرهٔ انتقال ویرایش/حذف می‌شود
    if (r.type === "transfer_in" || r.type === "transfer_out"
        || (r.type === "salary_paid" && r.payment_method === "transfer")) return setTransferOpen(true);
    /* v2.140.0 — کاربر: «هر سند همان‌جایی که ثبت شده باز شود». پرداختی که از «پرداخت حقوق» آمده ⇒ همان تب، همان ماه،
       و پنجرهٔ پرداخت‌های همان نفر روی همان پرداخت؛ «طلبِ حقوق» (بستنِ ماه) ⇒ همان تب. سندِ دستیِ «پرداخت حقوق»
       که از اسنادِ مالی/دفترِ حساب زده شده ⇒ همان پنجرهٔ سند (پایین). */
    if (r.type === "salary_payable" || (r.type === "salary_paid" && r.source_table === "payroll_payments")) {
      if (r.period_id) { setPeriodId(r.period_id); rememberPeriod(periods, r.period_id); }
      if (r.type === "salary_paid") setPayFocus({ employee_id: r.employee_id, payment_id: r.source_id, at: Date.now() });
      return setTab("payments");
    }
    // بقیهٔ اسناد: همان سندِ واحد در حالتِ ویرایش (مبلغ/تاریخ/مبدأ/شرح)
    setVoucher({
      id: r.id, doc_no: r.doc_no, employee_id: r.employee_id, transaction_type: r.type,
      amount: r.amount, occurred_at: r.date, description: r.description,
      payment_method: r.payment_method, reference_number: r.reference_number,
      source_account: r.source_account, petty_account_id: r.petty_account_id,
    });
  };

  const txnCols = [
    /* شمارهٔ سند اول از همه: حسابدار سند را با شماره صدا می‌زند، نه با ردیف. */
    { title: "شماره سند", dataIndex: "doc_no", width: 120, sorter: byStr("doc_no"),
      render: (v) => (v ? <code style={{ fontSize: 12 }}>{v}</code> : "—") },
    { title: "پرسنل", dataIndex: "employee_name", width: 160,
      sorter: byStr("employee_name"),
      render: (v, r) => (
        <a onClick={() => goLedger(r.employee_id)}
          title="دفترِ حسابِ همین شخص">{v || empName(r.employee_id)}</a>) },
    // v2.132.0: بیمه‌ای/غیربیمه‌ای و سمت
    { title: "بیمه", dataIndex: "insured_label", width: 100,
      filters: [{ value: "بیمه‌ای", text: "بیمه‌ای" }, { value: "غیربیمه‌ای", text: "غیربیمه‌ای" }],
      onFilter: (v, r) => r.insured_label === v,
      render: (v) => (v ? <Tag color={v === "بیمه‌ای" ? "blue" : "default"} style={{ margin: 0 }}>{v}</Tag> : "—") },
    { title: "سمت", dataIndex: "job_title", width: 120, sorter: byStr("job_title"),
      render: (v) => v || "—" },
    /* v2.133.0 — کاربر: «نوع دقیق بنویسد برای چی هست که بشه سورت کرد». متن از سرور می‌آید
       (مثلاً «پرداختِ حقوقِ شهریور — بیمه (بانکِ رسمی)»)؛ فیلتر روی «دسته»، سورت روی متن. */
    { title: "نوع", dataIndex: "type_label", width: 230, sorter: byStr("type_label"),
      filters: [...new Set(txns.map((r) => r.category).filter(Boolean))].map((c) => ({ text: c, value: c })),
      onFilter: (v, r) => r.category === v,
      render: (v, r) => (r.type === "debt_origination"
        ? <Tag color="purple" style={{ whiteSpace: "normal" }}>{v || "ماندهٔ ابتدای دوره"}</Tag>
        : <Tooltip title={`${r.category || ""} · ${KIND_LABEL[kindOfType(r.type)] || ""}`}>
            <Tag color={KIND_COLOR[kindOfType(r.type)]} style={{ whiteSpace: "normal" }}>
              {v || TXN_LABEL[r.type] || r.type}</Tag>
          </Tooltip>) },
    { title: "ثبت از", dataIndex: "origin", width: 130, sorter: byStr("origin"),
      filters: [...new Set(txns.map((r) => r.origin).filter(Boolean))].map((o) => ({ text: o, value: o })),
      onFilter: (v, r) => r.origin === v, render: originTag },
    { title: "اثر بر حساب", dataIndex: "direction", width: 110,
      render: v => v === "credit" ? <Tag color="green">+ بستانکار</Tag> : <Tag color="red">− بدهکار</Tag> },
    { title: "مبدأ / مقصد", dataIndex: "source_account", width: 120,
      render: (v, r) => (kindOfType(r.type) === "payroll" ? <Text type="secondary">—</Text>
        : <Tag color={v === "petty" ? "geekblue" : "default"}>{sourceLabel(v, r.petty_account_id)}</Tag>) },
    { title: "مبلغ (ریال)", dataIndex: "amount", align: "left", width: 130,
      sorter: byNum("amount"), render: v => fa(v) },
    /* «با احتسابِ این سند، حسابِ این نفر چقدر است» — خواستهٔ صریحِ کاربر.
       ماندهٔ per شخص است، نه سراسری؛ ماندهٔ سراسری در لیستِ چندنفره معنی ندارد. */
    { title: "مانده تا این سند", dataIndex: "running_balance", align: "left", width: 150,
      render: (v, r) => {
        if (r.kind === "adj") return <Tooltip title="داخلِ «طلبِ حقوقِ» همان ماه هنگامِ بستن حساب می‌شود؛ جدا در مانده اثر ندارد">
          <Text type="secondary">—</Text></Tooltip>;
        const n = Number(v) || 0;
        if (!n) return <Text type="secondary">۰</Text>;
        return <b style={{ color: n > 0 ? "#C0392B" : "#107C41" }}>
          {fa(Math.abs(n))} {Math.abs(n) < 1 ? "" : n > 0 ? "بدهکار" : "بستانکار"}</b>;
      } },
    { title: "وضعیت", dataIndex: "payment_status", width: 130,
      render: (v, r) => (v === "in_payroll"
        ? (r.payroll_closed ? <Tag color="green">در حقوقِ {r.period_label} لحاظ شد</Tag>
          : <Tag color="blue">در حقوقِ {r.period_label} (ماه هنوز بسته نشده)</Tag>)
        : v === "pending"
        ? <Tag color="orange">در انتظارِ پرداخت</Tag>
        : <Space size={4}><Tag color="green">پرداخت‌شده</Tag>
            {r.paid_at && <Text type="secondary" style={{ fontSize: 11 }}>{toJalali(r.paid_at)}</Text>}</Space>) },
    /* v2.144.0 — کاربر: «ترتیب‌ها هی به هم می‌ریزد». سندهای یک روز با سورتِ تاریخ جابه‌جا می‌شدند؛ حالا در یک روز همان ترتیبِ
       سرور می‌ماند و «مانده تا این سند» پشتِ‌سرِهم خوانده می‌شود. پیش‌فرض: جدید بالا. */
    { title: "تاریخ", dataIndex: "date", width: 110, defaultSortOrder: "descend",
      sorter: (a, b) => String(a.date || "").localeCompare(String(b.date || "")) || (b._seq - a._seq),
      render: (v) => toJalali(v) || "—" },
    { title: "شرح", dataIndex: "description", render: v => v || "—", ellipsis: true },
    { title: "اقدام", width: 150, fixed: "left", gxExport: false,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="ویرایش در همان بخشی که ثبت شده">
            <Button size="small" icon={<EditOutlined />} onClick={() => goSource(r)} />
          </Tooltip>
          {r.payment_status === "pending" && (
            /* تاریخِ پرداخت پرسیده می‌شود، نه فرض. پرداخت ممکن است دیروز
               انجام شده باشد و ثبتش امروز — سندی که تاریخش غلط باشد،
               صورت‌حسابِ بازه‌ای را خراب می‌کند. */
            <Button size="small" type="primary"
              onClick={() => setPayDate({ row: r, when: todayISO() })}>
              پرداخت شد
            </Button>
          )}
          {r.editable && (
            <Popconfirm title="حذف نرم؟" onConfirm={() => deleteFinanceTxn(r.id)
              .then(() => { message.success("حذف شد"); loadTxns(); })}>
              <Button size="small" danger type="text" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>) },
  ];

  const debtCols = [
    { title: "پرسنل", dataIndex: "employee_id", render: empName },
    { title: "مبلغ اولیه (ریال)", dataIndex: "amount", render: fa },
    { title: "مانده (ریال)", dataIndex: "remaining_amount", render: v => <b style={{ color: v > 0 ? "#cf1322" : "#389e0d" }}>{fa(v)}</b> },
    { title: "وضعیت", dataIndex: "status", render: v => ({
        open: <Tag color="red">باز</Tag>, paid: <Tag color="green">تسویه</Tag>,
        carried_forward: <Tag color="orange">منتقل‌شده</Tag>, cancelled: <Tag>لغو</Tag> }[v] || v) },
    { title: "عملیات", render: (_, d) => d.status === "open" && (
      <Space>
        <Button size="small" onClick={() => doRepay(d)}>بازپرداخت</Button>
        <Button size="small" onClick={() => doCarry(d)}>انتقال به ماه بعد</Button>
      </Space>) },
  ];

  const periodSelector = (
    <Select value={periodId} onChange={(v) => { setPeriodId(v); rememberPeriod(periods, v); }} style={{ width: 130 }} placeholder="دوره">
      {periods.map(p => <Option key={p.id} value={p.id}>{periodLabel(p)}</Option>)}
    </Select>
  );

  const tabs = [
    {
      key: "balances", label: "مانده و وضعیت حساب",
      children: <BalancesTab onOpenLedger={goLedger} active={activeTab === "balances"} />,
    },
    {
      key: "payments", label: "پرداخت حقوق",
      children: <SalaryPaymentsTab periodId={periodId} periodSelector={periodSelector}
        canEdit={canEdit} focus={payFocus} active={activeTab === "payments"} />,
    },
    {
      key: "advances", label: "مساعده (دسته‌جمعی)",
      children: <AdvanceBatchTab employees={employees} periodId={periodId}
        onDone={() => { loadTxns(); }} />,
    },
    {
      key: "txns", label: "اسناد مالی",
      children: (
        <Card title="دفترِ اسنادِ مالی — همهٔ پرداخت‌ها، بدهکاری‌ها و بستانکاری‌ها"
          extra={<Space wrap>
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>بازه</Text>
              <Select size="small" style={{ width: 150 }} value={txnRange}
                onChange={setTxnRange} options={TXN_RANGES} />
            </Space>
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>پرسنل</Text>
              <Select size="small" showSearch allowClear style={{ width: 220 }}
                placeholder="جستجوی نام یا کد…" value={txnEmp} onChange={setTxnEmp}
                optionFilterProp="label" filterOption={filterByChildren}
                options={employees.map((e) => ({
                  value: e.id,
                  label: `${e.full_name}${e.employee_code ? ` (${e.employee_code})` : ""}`,
                }))} />
            </Space>
            {txnEmp && (
              <Button size="small" type="link"
                onClick={() => goLedger(txnEmp)}>دفترِ حسابِ این شخص</Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={loadTxns}>بازخوانی</Button>
            <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>انتقال بین پرسنل</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setVoucher({})}>سند پرداخت / دریافت</Button>
          </Space>}>
          <TransferModal open={transferOpen} onClose={() => setTransferOpen(false)} onDone={loadTxns} />
          <Alert type="info" showIcon style={{ marginBottom: 10 }}
            message="هر سندی که ثبت شده اینجاست — مساعده، بازپرداختِ بدهی، پرداخت، هزینه‌ها و ماندهٔ ابتدای دوره."
            description="هر سند شمارهٔ حسابداریِ خودش را دارد (مثلاً MS0506-012: نوع + سال/ماهِ شمسی + ترتیبِ همان ماه). ستونِ «مانده تا این سند» می‌گوید حسابِ همان شخص با احتسابِ همین سند چقدر شده. سندِ «در انتظارِ پرداخت» در حسابِ کسی نمی‌نشیند تا پرداختش ثبت شود. دکمهٔ ویرایش شما را به همان بخشی می‌برد که سند آنجا ثبت شده." />
          {docTotals && (
            <Space wrap style={{ marginBottom: 10 }}>
              <Tag>{fa(docTotals.count)} سند</Tag>
              <Tag color="red">جمع بدهکار: {fa(docTotals.debit)}</Tag>
              <Tag color="green">جمع بستانکار: {fa(docTotals.credit)}</Tag>
              {docTotals.pending > 0 && (
                <Tag color="orange">
                  {fa(docTotals.pending)} در انتظارِ پرداخت — {fa(docTotals.pending_amount)} ریال
                </Tag>
              )}
            </Space>
          )}
          <GxTable layoutId="finance-documents" dataSource={txns} columns={txnCols}
            rowKey={(r) => `${r.kind}-${r.id}`} size="small"
            gxExportName="اسناد-مالی" gxLayoutVersion="v2-docs"
            pagination={{ pageSize: 50 }} scroll={{ x: "max-content" }} />
        </Card>
      ),
    },
    {
      key: "debts_legacy", label: "بدهی‌ها", forceRender: false,
      children: (
        <Card title="بدهی‌های دوره"
          extra={<Space>{periodSelector}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setDebtModal(true)}>ثبت بدهی</Button></Space>}>
          <GxTable dataSource={debts} columns={debtCols} rowKey="id" size="small" pagination={false} />
        </Card>
      ),
    },
    {
      key: "petty", label: "تنخواه",
      children: <PettyCashTab employees={employees} />,
    },
    {
      // v2.173.0 — تراز آزمایشیِ حقوق: «دوبرابر نشه، چیزی کم نباشه، چیزی زیاد شد مشخص بشه»
      key: "health", label: "سلامت حساب‌ها",
      children: <LedgerHealthTab onOpenLedger={goLedger} active={activeTab === "health"} />,
    },
    {
      key: "reports", label: "گزارش‌ها",
      children: <FinanceReportsTab periodId={periodId} periodSelector={periodSelector} />,
    },
    {
      key: "ledger", label: "دفتر حساب شخص",
      children: (
        <Card title="دفتر حساب پرسنل"
          extra={
            <Space wrap>
              <Select showSearch placeholder="انتخاب پرسنل" style={{ width: 240 }} optionFilterProp="children"
                value={ledgerEmp} onChange={goLedger}
                filterOption={filterByChildren}>
                {employees.map(e => <Option key={e.id} value={e.id}>{e.full_name} ({e.employee_code})</Option>)}
              </Select>
              {/* بازه، نه فقط «از تاریخ» — خواستهٔ کاربر: «صورت‌حساب مرداد،
                  شهریور… از اول شهریور تا اون بازه‌ای که هستیم». بدونِ «تا
                  تاریخ»، صورت‌حسابِ وسطِ ماه ممکن نبود. */}
              <Space size={4}>
                <span style={{ fontSize: 12, color: "#888" }}>از</span>
                <JalaliDateInput value={ledgerFrom} allowClear style={{ width: 128 }}
                  onChange={setLedgerFrom} />
                <span style={{ fontSize: 12, color: "#888" }}>تا</span>
                <JalaliDateInput value={ledgerTo} allowClear style={{ width: 128 }}
                  onChange={setLedgerTo} />
              </Space>
              {/* صورتحسابِ مانده ≠ فیشِ حقوق: این یکی لحظه‌ای است و به دوره
                  وابسته نیست، پس هر وقت طرف‌حساب بپرسد «چقدر حسابم است؟»
                  می‌شود گرفت — حتی وقتی حقوقِ ماه هنوز بسته نشده. */}
              {/* ثبتِ پرداخت **همین‌جا** — خواستهٔ کاربر: جایی که صورت‌حساب را
                  می‌بیند باید بتواند پرداخت را هم بزند، نه اینکه برگردد به
                  لیستِ مانده و نفر را دوباره پیدا کند. */}
              <Button type="primary" icon={<DollarOutlined />} disabled={!ledgerEmp}
                onClick={() => {
                  const bal = Number(ledger?.balance || 0);
                  setVoucher({
                    employee_id: ledgerEmp, kind: bal > 0 ? "receive" : "pay",
                    transaction_type: bal > 0 ? "cash_repayment" : "bank_transfer",
                    amount: Math.abs(bal) || null,
                    description: bal > 0 ? "بازپرداخت بدهی" : "پرداخت به پرسنل",
                  });
                }}>
                ثبت پرداخت
              </Button>
              <Button icon={<FilePdfOutlined />} disabled={!ledgerEmp}
                onClick={() => pdf.open({
                  url: `/api/v1/finance/employee/${ledgerEmp}/statement.pdf`,
                  params: {
                    ...(ledgerFrom ? { from_date: ledgerFrom } : {}),
                    ...(ledgerTo ? { as_of: ledgerTo } : {}),
                  },
                  title: "صورتحساب مالی", fallbackName: `statement_${ledgerEmp}.pdf`,
                  storageKey: "statement", defaultPage: "A4",
                })}>
                صورتحساب PDF
              </Button>
            </Space>}>
          {ledger ? (
            <>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col><Statistic title="جمع بدهکار" value={fa(ledger.total_debit)} valueStyle={{ color: "#cf1322" }} /></Col>
                <Col><Statistic title="جمع بستانکار" value={fa(ledger.total_credit)} valueStyle={{ color: "#389e0d" }} /></Col>
                <Col><Statistic title="مانده" value={fa(Math.abs(ledger.balance))}
                  prefix={<DollarOutlined />}
                  suffix={ledger.balance >= 1 ? "بدهکار" : ledger.balance <= -1 ? "بستانکار" : ""}
                  valueStyle={{ color: ledger.balance > 0 ? "#C0392B" : ledger.balance < 0 ? "#107C41" : undefined }} /></Col>
                {ledger.opening_balance ? (
                  <Col><Statistic title="ماندهٔ ابتدای بازه" value={fa(ledger.opening_balance)} /></Col>
                ) : null}
              </Row>
              <GxTable layoutId="finance-person-ledger" dataSource={ledger.rows || []}
                columns={ledgerCols} rowKey={(r) => `${r.kind}-${r.id}`} size="small"
                gxExportName="employee-ledger"
                pagination={{ pageSize: 30 }} scroll={{ x: "max-content" }} />
            </>
          ) : <div style={{ color: "#999", padding: 20, textAlign: "center" }}>یک پرسنل انتخاب کنید.</div>}
        </Card>
      ),
    },
  ];

  return (
    <div>
      <Title level={5} style={{ marginBottom: 8 }}>مالی پرسنل</Title>
      <Alert type="info" showIcon closable style={{ marginBottom: 12 }}
        message="مانده و بدهیِ هر نفر، هزینه‌هایش، و پرداختِ حقوقِ دوره همه اینجاست. بیمه صفحهٔ جدای خودش را دارد." />
      <GxTabs activeKey={activeTab} onChange={setTab} items={tabs} />

      <Modal open={!!payDate} title={`تاریخِ پرداخت — ${payDate?.row?.doc_no || ""}`}
        onCancel={() => setPayDate(null)} okText="ثبتِ پرداخت" cancelText="انصراف"
        onOk={() => setTxnPaymentStatus({
          ids: [payDate.row.id], paid: true, paid_at: payDate.when || todayISO(),
          ...(payDate.source || {}),
        }).then(() => {
          message.success("پرداخت ثبت شد"); setPayDate(null); loadTxns();
        }).catch(() => message.error("ثبت نشد"))}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary">
            {payDate?.row?.employee_name} — {fa(payDate?.row?.amount)} ریال
          </Text>
          <Text>پول در چه تاریخی پرداخت شد؟</Text>
          <JalaliDateInput style={{ width: "100%" }} value={payDate?.when}
            onChange={(v) => setPayDate((p) => ({ ...p, when: v }))} />
          <Text>از کجا پرداخت شد؟</Text>
          <SourceSelect style={{ width: "100%" }}
            value={payDate?.source || { source_account: payDate?.row?.source_account, petty_account_id: payDate?.row?.petty_account_id }}
            onChange={(v) => setPayDate((p) => ({ ...p, source: v }))} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            پیش‌فرض امروز است؛ اگر پرداخت روزِ دیگری بوده همین‌جا عوضش کنید.
            سند با همین تاریخ در حسابِ شخص می‌نشیند.
          </Text>
        </Space>
      </Modal>

      <VoucherModal open={!!voucher} initial={voucher || {}} employees={employees} periodId={periodId}
        onClose={() => setVoucher(null)} onSaved={afterVoucher} />

      <Modal open={!!repayFor} title={`بازپرداخت بدهی — ${repayFor ? empName(repayFor.employee_id) : ""}`}
        onCancel={() => setRepayFor(null)} onOk={() => repayForm.submit()} okText="ثبت" cancelText="انصراف" destroyOnHidden>
        {repayFor && <Text type="secondary">ماندهٔ بدهی: {fa(repayFor.remaining_amount)} ریال</Text>}
        <Form form={repayForm} layout="vertical" onFinish={submitRepay} style={{ direction: "rtl", marginTop: 8 }}>
          <Form.Item name="amount" label="مبلغ بازپرداخت (ریال)" rules={[{ required: true }]}><MoneyInput /></Form.Item>
          <Form.Item name="occurred_at" label="تاریخ"><JalaliDateInput style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="source" label="واریز به حساب" extra="تنخواه ⇒ صندوق شارژ می‌شود"><SourceSelect style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="note" label="شرح"><Input /></Form.Item>
        </Form>
      </Modal>

      <Modal title="ثبت بدهی" open={debtModal} onCancel={() => setDebtModal(false)}
        onOk={() => debtForm.submit()} okText="ثبت" cancelText="انصراف">
        <Form form={debtForm} layout="vertical" onFinish={submitDebt} style={{ direction: "rtl" }}>
          <Form.Item name="employee_id" label="پرسنل" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children"
              filterOption={filterByChildren}>
              {employees.map(e => <Option key={e.id} value={e.id}>{e.full_name} ({e.employee_code})</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="مبلغ بدهی (ریال)" rules={[{ required: true }]}>
            <MoneyInput previewLabel="مبلغ نهایی" />
          </Form.Item>
          <Form.Item name="note" label="توضیح"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
      {pdf.node}
    </div>
  );
}
